import { observer } from 'mobx-react-lite';
import ChartInstalledWithin24Hours from '../components/chart/ChartInstalledWithin24Hours';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/hooks/useStore';
import { useEffect } from 'react';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import Table from '@/components/table/Table';
import { DEFAULT_SELECT_PAGE_SIZES, DISPPLAY_MOMENT_YMD_HM } from '@/configs/constants';
import { formatDateTime } from '@/utils/datetime';
import { VehicleToArrangeIn24h } from '@/types/dashboard';
import { Link } from 'react-router-dom';
import { getLinkAccountDetail, getLinkBikeDetail, getLinkLocationDetail, getLinkOrderDetail } from '@/utils/utils';

export default observer(function InstalledWithin24Hours() {
    // hooks
    const { t } = useTranslation();

    // store
    const {
        dashboardStore: {
            getVehiclesToArrangeIn24h,
            vehiclesToArrangeIn24h,
            pagingVehiclesToArrangeIn24h,
            totalVehiclesToArrangeIn24h,
            cleanVehiclesToArrangeIn24h
        }
    } = useStore();

    useEffect(() => {
        getVehiclesToArrangeIn24h();

        return () => {
            cleanVehiclesToArrangeIn24h();
        };
    }, []);

    // functions
    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging =
            stateUpdater instanceof Function ?
                stateUpdater(pagingVehiclesToArrangeIn24h) :
                stateUpdater;
        getVehiclesToArrangeIn24h({}, newPaging);
    };

    // columns
    const columns: ColumnDef<VehicleToArrangeIn24h>[] = [
        {
            accessorKey: 'orderCodeSystem',
            header: t('field.reservation_code'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <span className='min-w-[100px]'>
                    {original.orderCodeSystem?.length > 0 && (
                        <Link
                            to={getLinkOrderDetail(original.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.orderCodeSystem}
                        </Link>
                    )}
                </span>
            )
        },
        {
            accessorKey: 'location',
            header: t('field.location_name'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[100px] line-clamp-2'>
                    {original.location?.name?.length > 0 && (
                        <Link
                            to={getLinkLocationDetail(original.location.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.location?.name}
                        </Link>
                    )}
                </div>
            )
        },
        {
            accessorKey: 'preparationTime',
            header: t('field.installation_deadline'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>
                    {formatDateTime(original.preparationTime, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}
                </div>
            )
        },
        {
            accessorKey: 'rentalFrom',
            header: t('field.rental_date_and_time'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>
                    {formatDateTime(original.rentalFrom, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}
                </div>
            )
        },
        {
            accessorKey: 'rentalTo',
            header: t('field.return_date_and_time'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>
                    {formatDateTime(original.rentalTo, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}
                </div>
            )
        },
        {
            accessorKey: 'bike',
            header: t('field.model_bike'),
            size: 250,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[100px] line-clamp-2'>
                    {original.bike?.bikeModel?.length > 0 && (
                        <Link
                            to={getLinkBikeDetail(original.bike.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.bike?.bikeModel}
                        </Link>
                    )}
                </div>
            )
        },
        {
            accessorKey: 'renter',
            header: t('field.customer_name'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[90px] line-clamp-2'>
                    {original.renter?.fullName?.length > 0 && (
                        <Link
                            to={getLinkAccountDetail(original.renter.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.renter?.fullName}
                        </Link>
                    )}
                </div>
            )
        },
        {
            accessorKey: 'owner',
            header: t('field.owner'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[90px] line-clamp-2'>
                    {original.owner?.fullName?.length > 0 && (
                        <Link
                            to={getLinkAccountDetail(original.owner.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.owner?.fullName}
                        </Link>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className='layout-card'>
            <div className='mt-8 mb-12'>
                <ChartInstalledWithin24Hours />
            </div>

            <Table
                columns={columns}
                data={vehiclesToArrangeIn24h}
                state={pagingVehiclesToArrangeIn24h}
                rowCount={totalVehiclesToArrangeIn24h}
                onStateChange={onStateChange}
                selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                getRowId={row => row.id?.toString()}
                showSelectPageSize={false}
            />
        </div>
    );
});
